import { Minus, Plus, Printer, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { adminApi } from "../../api/client";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useToast } from "../../context/ToastContext";
import type { EventItem, Order } from "../../types/api";
import { formatCurrency } from "../../utils/format";

type IssuePaymentMethod = "cash" | "card" | "mobile-money" | "bank-transfer" | "comp";

const ISSUE_PAYMENT_METHODS: Array<{ value: IssuePaymentMethod; label: string }> = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card (offline)" },
  { value: "mobile-money", label: "Mobile money" },
  { value: "bank-transfer", label: "Bank transfer" },
  { value: "comp", label: "Comp / Complimentary" },
];

type DraftItem = {
  ticketTierId: string;
  quantity: number;
};

export default function IssueTicketsDialog({
  event,
  isOpen,
  onClose,
  onIssued,
}: {
  event: EventItem;
  isOpen: boolean;
  onClose: () => void;
  onIssued?: (order: Order) => void;
}) {
  const { token } = useAdminAuth();
  const { showToast } = useToast();
  const [items, setItems] = useState<DraftItem[]>([]);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [paymentMethod, setPaymentMethod] = useState<IssuePaymentMethod>("cash");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const wasOpen = useRef(false);

  useEffect(() => {
    // Reset state only on the closed -> open transition. The parent dashboard
    // refreshes its event prop every 15s; without this guard, every refresh
    // would wipe the user's in-progress quantities.
    if (isOpen && !wasOpen.current) {
      setItems(
        event.ticketTiers.map((tier) => ({
          ticketTierId: tier.id,
          quantity: 0,
        }))
      );
      setCustomer({ name: "", email: "", phone: "" });
      setPaymentMethod("cash");
      setNotes("");
    }
    wasOpen.current = isOpen;
  }, [event.ticketTiers, isOpen]);

  const tierLookup = useMemo(() => {
    const lookup = new Map<string, EventItem["ticketTiers"][number]>();
    event.ticketTiers.forEach((tier) => lookup.set(tier.id, tier));
    return lookup;
  }, [event.ticketTiers]);

  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => {
      const tier = tierLookup.get(item.ticketTierId);
      if (!tier) return sum;
      return sum + tier.price * item.quantity;
    }, 0);
    return { subtotal, total: paymentMethod === "comp" ? 0 : subtotal };
  }, [items, paymentMethod, tierLookup]);

  if (!isOpen) return null;

  const setQuantity = (tierId: string, value: number) => {
    setItems((current) =>
      current.map((item) =>
        item.ticketTierId === tierId
          ? { ...item, quantity: Math.max(0, Math.min(value, tierLookup.get(tierId)?.quantity ?? value)) }
          : item
      )
    );
  };

  const handleSubmit = async () => {
    if (!token) return;

    const selected = items.filter((item) => item.quantity > 0);
    if (selected.length === 0) {
      showToast("Pick at least one ticket", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const { order } = await adminApi.issueTickets(token, event.id, {
        items: selected.map((item) => ({
          ticketTierId: item.ticketTierId,
          quantity: item.quantity,
        })),
        customer: {
          name: customer.name.trim() || undefined,
          email: customer.email.trim() || undefined,
          phone: customer.phone.trim() || undefined,
        },
        paymentMethod,
        notes: notes.trim() || undefined,
      });

      showToast(
        `Issued ${totalQuantity} ticket${totalQuantity === 1 ? "" : "s"} · ${order.reference}`,
        "success"
      );
      onIssued?.(order);
      onClose();
      window.open(`/admin/orders/${order.reference}/print`, "_blank");
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Could not issue tickets";
      showToast(message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch bg-black/60 p-3 backdrop-blur-md dark:bg-black/75 sm:p-6">
      <div className="mx-auto flex h-full w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl">
        <div className="flex items-start justify-between border-b border-line px-6 py-5">
          <div>
            <span className="ego-badge ego-badge-brand">In-person sale</span>
            <h2 className="mt-2 text-xl font-bold tracking-tight text-foreground">Issue tickets</h2>
            <p className="text-sm text-muted-foreground">{event.title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition hover:bg-surface-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          <section>
            <h3 className="ego-eyebrow">Tickets</h3>
            <div className="mt-3 space-y-2">
              {event.ticketTiers.map((tier) => {
                const draft = items.find((item) => item.ticketTierId === tier.id);
                const remaining = Math.max(tier.quantity - tier.sold, 0);
                const quantity = draft?.quantity ?? 0;
                const lineTotal = paymentMethod === "comp" ? 0 : tier.price * quantity;
                return (
                  <div
                    key={tier.id}
                    className="flex items-center gap-3 rounded-xl border border-line bg-surface p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground">{tier.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatCurrency(tier.price)} · {remaining} left
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setQuantity(tier.id, quantity - 1)}
                        disabled={quantity <= 0}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-foreground transition hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="Decrease"
                      >
                        <Minus size={14} />
                      </button>
                      <input
                        type="number"
                        min={0}
                        max={remaining}
                        value={quantity}
                        onChange={(inputEvent) =>
                          setQuantity(tier.id, Number(inputEvent.target.value) || 0)
                        }
                        className="h-8 w-14 rounded-lg border border-line bg-surface text-center text-sm font-semibold text-foreground outline-none focus:border-[var(--pp-gold)]"
                      />
                      <button
                        type="button"
                        onClick={() => setQuantity(tier.id, quantity + 1)}
                        disabled={quantity >= remaining}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-foreground transition hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="Increase"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <div className="w-20 text-right text-sm font-bold text-foreground">
                      {formatCurrency(lineTotal)}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <h3 className="ego-eyebrow">Customer (optional)</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="block sm:col-span-2">
                <span className="mb-1 block text-xs font-semibold text-muted-foreground">Name</span>
                <input
                  type="text"
                  value={customer.name}
                  onChange={(inputEvent) =>
                    setCustomer((current) => ({ ...current, name: inputEvent.target.value }))
                  }
                  placeholder="Walk-up customer"
                  className="ego-input !py-2"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted-foreground">Email</span>
                <input
                  type="email"
                  value={customer.email}
                  onChange={(inputEvent) =>
                    setCustomer((current) => ({ ...current, email: inputEvent.target.value }))
                  }
                  className="ego-input !py-2"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted-foreground">Phone</span>
                <input
                  type="tel"
                  value={customer.phone}
                  onChange={(inputEvent) =>
                    setCustomer((current) => ({ ...current, phone: inputEvent.target.value }))
                  }
                  className="ego-input !py-2"
                />
              </label>
            </div>
          </section>

          <section>
            <h3 className="ego-eyebrow">Payment</h3>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {ISSUE_PAYMENT_METHODS.map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => setPaymentMethod(method.value)}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${
                    paymentMethod === method.value
                      ? "border-[var(--pp-gold)] bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]"
                      : "border-line bg-surface text-muted-foreground hover:bg-surface-muted"
                  }`}
                >
                  {method.label}
                </button>
              ))}
            </div>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-semibold text-muted-foreground">Notes (optional)</span>
              <input
                type="text"
                value={notes}
                onChange={(inputEvent) => setNotes(inputEvent.target.value)}
                placeholder="e.g. cash received from John at gate"
                className="ego-input !py-2"
              />
            </label>
          </section>
        </div>

        <div className="border-t border-line bg-surface-muted px-6 py-4">
          <div className="mb-3 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {totalQuantity} ticket{totalQuantity === 1 ? "" : "s"}
            </span>
            <span className="text-lg font-bold text-foreground">{formatCurrency(totals.total)}</span>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="ego-btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || totalQuantity === 0}
              className="ego-btn-primary flex-1"
            >
              <Printer size={15} />
              {isSubmitting ? "Issuing..." : "Issue & print"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
